import { sectionRegistry } from './sectionRegistry';
import { useEffect, useMemo, useRef } from 'react';
import {
  type Block,
  type MenuEntry,
  type PageDocument,
  type SectionDefinition,
} from '@aquilixshop/ui-react';
import { serializeComposed, useBuilderStore } from '../state/builderStore';
import { getMenuEntries, listMenus } from '../lib/menusApi';

/** Joins/splits the collected handles — a space can't appear in a menu handle, so it is a safe separator. */
const SEP = ' ';

/**
 * Resolve the document's PICKED navigation menus for the EDIT canvas.
 *
 * The header/footer follow Shopify's model: the seller picks a menu by handle (`menu` field) and the section
 * renders `RenderContext.resolved.menus[handle]`. The storefront gets that map from the page bundle. The
 * builder had NO equivalent step — `buildEditContext` returned `resolved: {}` — so the moment a seller picked
 * "Menu principal" the canvas nav went EMPTY: the section's `link` blocks are only the fallback for an
 * *unpicked* menu, and a picked-but-unresolved handle renders nothing. This hook closes that gap.
 *
 * Design notes (mirrors `usePickedProducts`):
 *  - The HANDLE LIST drives the effect, so typing in a text field re-runs nothing.
 *  - The store's menus are listed once to map handle → id (the detail endpoint is keyed by id).
 *  - Each handle is attempted at most once per session, so a deleted/renamed menu can't spin a fetch loop.
 *  - The whole cache is re-applied whenever the render context is rebuilt (theme undo, page switch, binding a
 *    preview product all mint a fresh context that drops `resolved`) — `mergeResolvedMenus` no-ops when
 *    nothing changed, so that self-healing loop settles after one pass.
 *  - Best-effort: an offline/5xx leaves the section on its `link`-block fallback. The canvas never throws.
 *  - A fetch is NEVER abandoned because the effect re-ran. It writes into the global store, not component
 *    state, and `attempted` means nobody will fetch those handles again — dropping the result stranded the
 *    menus in the cache and left the nav empty for the whole session.
 */
export function useResolvedMenus(): void {
  const order = useBuilderStore((s) => s.order);
  const sectionsById = useBuilderStore((s) => s.sectionsById);
  const meta = useBuilderStore((s) => s.meta);
  const renderContext = useBuilderStore((s) => s.renderContext);
  const mergeResolvedMenus = useBuilderStore((s) => s.mergeResolvedMenus);

  const handlesKey = useMemo(
    // COMPOSED (body + global chrome): the `menu` pick lives on the HEADER section, which is part of the
    // store-wide chrome — `serialize` (the save shape) omits it, so menus would never resolve.
    () => collectMenuHandles(serializeComposed({ order, sectionsById, meta })).join(SEP),
    [order, sectionsById, meta],
  );
  const handles = useMemo(() => (handlesKey ? handlesKey.split(SEP) : []), [handlesKey]);

  const cache = useRef(new Map<string, readonly MenuEntry[]>());
  const attempted = useRef(new Set<string>());

  useEffect(() => {
    if (handles.length === 0) return;

    const known = (list: string[]): Record<string, readonly MenuEntry[]> => {
      const out: Record<string, readonly MenuEntry[]> = {};
      for (const handle of list) {
        const entries = cache.current.get(handle);
        if (entries) out[handle] = entries;
      }
      return out;
    };

    // Re-apply what is already cached first (covers a context rebuild, a page switch back, an undo).
    mergeResolvedMenus(known(handles));

    const missing = handles.filter((h) => !cache.current.has(h) && !attempted.current.has(h));
    if (missing.length === 0) return;
    for (const h of missing) attempted.current.add(h);

    void (async () => {
      let byHandle: Map<string, string>;
      try {
        byHandle = new Map((await listMenus()).map((m) => [m.handle, m.id]));
      } catch {
        // Offline / server error: allow a later edit to retry these handles rather than sticking forever.
        for (const h of missing) attempted.current.delete(h);
        return;
      }
      await Promise.all(
        missing.map(async (handle) => {
          const id = byHandle.get(handle);
          if (!id) return; // handle no longer exists → the section keeps its fallback links
          try {
            cache.current.set(handle, await getMenuEntries(id));
          } catch {
            // Deleted between the list and the read, or a server error → fallback, never retried.
          }
        }),
      );
      // Merge UNCONDITIONALLY. This used to be gated on a `cancelled` flag set by the effect's cleanup —
      // but that cleanup fires on every RE-RUN (a context rebuild, a doc edit, a selection), not only on
      // unmount, and the canvas always re-runs at least once while this fetch is in flight. The result was
      // dropped, and because `attempted` already held these handles no later run re-fetched OR re-applied
      // them: the menus sat in the cache while the header rendered an empty nav for the rest of the
      // session. Writing into the global store after an unmount is harmless — it is not component state,
      // and `mergeResolvedMenus` no-ops when nothing changed.
      mergeResolvedMenus(known(missing));
    })();
  }, [handles, renderContext, mergeResolvedMenus]);
}

/**
 * Every non-empty value of a `menu`-typed field in the document, deduped. Menu picks are plain STRINGS on the
 * wire (unlike product/collection refs, which are tagged objects), so the walk is schema-driven: it asks the
 * registry which fields of a section / block type are menu pickers.
 */
export function collectMenuHandles(doc: PageDocument): string[] {
  const out = new Set<string>();

  const take = (value: unknown): void => {
    if (typeof value === 'string' && value.trim()) out.add(value.trim());
  };

  const scanBlocks = (
    blocks: Block[] | undefined,
    fieldsOf: (type: string) => readonly string[],
  ): void => {
    for (const b of blocks ?? []) {
      for (const id of fieldsOf(b.type)) take(b.settings[id]);
      scanBlocks(b.blocks, fieldsOf);
    }
  };

  for (const section of doc.sections) {
    const def: SectionDefinition | undefined = sectionRegistry[section.type];
    if (!def) continue;
    for (const f of def.schema.fields) if (f.type === 'menu') take(section.settings[f.id]);
    // Block schemas are declared on the SECTION (blocks aren't globally typed), so resolve them from `def`.
    const blockFields = (type: string): readonly string[] =>
      (def.schema.blocks ?? [])
        .find((b) => b.type === type)
        ?.fields.filter((f) => f.type === 'menu')
        .map((f) => f.id) ?? [];
    scanBlocks(section.blocks, blockFields);
  }

  return [...out];
}
